/**
 * Explanations for every field on the Org Config page.
 *
 * Kept in one file rather than scattered through a 1,500-line page so the wording
 * can be written and reviewed as a body of text, and so a new field is obviously
 * missing an explanation. Each entry answers three questions:
 *
 *   what     — what the field controls
 *   breaks   — what goes wrong when it is set incorrectly (omit if nothing does)
 *   example  — a real value from an existing org, never a made-up one
 *
 * Keys match the field key used on the page. Org-level fields are top-level here;
 * fields inside the pipeline config file (org_config.json) live under `pipeline`.
 */

export const ORG_FIELD_HELP = {
  // ── Phase 1: core identity ────────────────────────────────────────────────
  org_key: {
    what: 'The short, lowercase name that identifies this org everywhere — its blob container, its config folder, and the ORG_NAME the pipeline runs under.',
    breaks: 'It cannot be changed after creation without recreating the blob container and re-pointing the pipeline. Get it right first time.',
    example: 'syngentaind',
  },
  display_name: {
    what: 'The name people see in the dashboard header and the org picker.',
    example: 'Syngenta Indonesia',
  },
  type: {
    what: 'Which dashboard this org gets. Sales shows Home / Field Signal / Field Intel / Field Ops / Field Strategy; Agriculture shows Home / Marketing / Operations / Engagement / Audio.',
    breaks: 'The wrong type gives the org a set of tabs whose queries expect columns its data never populates, so pages render empty.',
    example: 'agri',
  },
  database: {
    what: 'The PostgreSQL database this org reads and writes. Created automatically when you save a new org.',
    breaks: 'Pointing two orgs at one database merges their data, and the dashboard has no way to separate it afterwards.',
    example: 'syngentaind',
  },
  schema_source: {
    what: 'The existing organization whose live database the new one copies its structure from: tables, views, indexes and the fixed lookup rows. No customer data is copied.',
    breaks: 'If the org you pick is missing a database migration, the new org will be missing it too. Pick one that is up to date.',
    example: 'syngentaind',
  },
  company_code: {
    what: 'Short code used to match this org to its recordings in the shared upload containers.',
    example: 'SYN',
  },
  user_id_base: {
    what: 'The starting number for this org\'s field-agent user IDs. Each org gets its own block so IDs never collide across orgs.',
    breaks: 'Overlapping blocks make two orgs\' agents share IDs, which silently attributes one org\'s calls to the other\'s agents.',
    example: '3000',
  },
  container: {
    what: 'The Azure Blob container holding this org\'s recordings, transcriptions and config. Defaults to the org key.',
    breaks: 'If it does not match the container the mobile app uploads to, recordings arrive somewhere the pipeline never looks.',
    example: 'syngentaind',
  },
  tabs: {
    what: 'Which dashboard modules this org can see. Drag from the module list to add.',
    breaks: 'A tab whose data the pipeline does not produce for this org shows an empty page rather than an error.',
    example: 'agri_home, agri_marketing, agri_operations',
  },
  opportunity_entity_types: {
    what: 'Entity types counted as sales opportunities when scoring conversations in Field Strategy.',
    breaks: 'Names must match dim_entity_type exactly; a typo scores nothing and reports zero opportunities.',
    example: 'Crop Issues, Product Interest',
  },
  pain_entity_types: {
    what: 'Entity types treated as customer pain in Field Signal and Field Intel.',
    breaks: 'Same as above — an unmatched name silently contributes nothing to pain detection.',
    example: 'Crop Issues, Pricing',
  },

  // ── Phase 7: production switches (companies/whisper_orgs.json) ────────────
  // Not called `pipeline` — that key below holds the org_config.json fields.
  transcribe_recordings: {
    what: 'Whether Whisper (on its own server) turns this org\'s new recordings into transcripts. Saved as "active" in companies/whisper_orgs.json. Saved as soon as you change it — there is no Save button.',
    breaks: 'Off: recordings are never transcribed, so Run Pipeline has nothing to process.',
    example: 'syngentaind: on',
  },
  run_pipeline: {
    what: 'Whether the production cron pulls entities out of this org\'s new transcripts and loads them into its database. Saved as "entity_active" in companies/whisper_orgs.json. Saved as soon as you change it.',
    breaks: 'Turned on before the database, config files and agents are ready: every cron run for this org fails.',
    example: 'syngentaind: on, coromandel: off',
  },

  // ── Pipeline config (org_config.json) ─────────────────────────────────────
  pipeline: {
    db_name: {
      what: 'The database the pipeline writes to for this org. Set when the org is created and shown here for reference.',
    },
    container: {
      what: 'The blob container the pipeline reads transcripts from and writes processed files back to.',
    },
    customer_type: {
      what: 'Shapes what the pipeline extracts. Agriculture looks for crops, pests and products; Sales looks for roof issues, materials and quotes; Exit Interview looks for reasons for leaving.',
      breaks: 'The wrong type runs agents whose prompts do not match the conversations, producing confident but irrelevant extractions.',
      example: 'agriculture',
    },
    country: {
      what: 'Drives the postcode format the location resolver expects.',
      breaks: 'A mismatch makes every postcode fail to parse, so conversations never get a region and regional pages stay empty.',
      example: 'ID',
    },
    enable_location_resolver: {
      what: 'Turns on GPS-to-region lookup via the Geoapify API. US-oriented; most orgs resolve region from call metadata instead.',
      breaks: 'Enabling it without a GEOAPIFY_API_KEY makes every lookup fail and slows the pipeline down for nothing.',
    },
    call_center_system_user_id: {
      what: 'The user ID the call-centre system uses for its own automated uploads, so those are not counted as a field agent.',
      breaks: 'Unset, the system account appears in the dashboard as the most productive agent in the org.',
    },
    cron_mkdir: {
      what: 'Directories created on the pipeline machine before each run. Only needed for orgs whose transcripts arrive on disk rather than through blob.',
      example: 'data/syngentaind/transcripts/new',
    },
    cron_enabled: {
      what: 'Whether the scheduled pipeline picks this org up at all. The schedule runs every 15 minutes and asks each org whether it is due.',
      breaks: 'Turned off, nothing is processed for this org — new recordings pile up unread with no error anywhere.',
    },
    cron_interval_minutes: {
      what: 'How long to wait after a run before running this org again. 0 means every 15-minute tick.',
      breaks: 'Set too low for a large org and the next run starts while the previous is still going; the pipeline skips it, so a big number here is not a slowdown.',
      example: '0 for continuous, 60 for hourly, 1440 for daily',
    },
    default_llm: {
      what: 'The model the extraction agents use unless an agent overrides it.',
      example: 'qwen3:14b',
    },
    default_llm_provider: {
      what: 'Where that model runs. "ollama" uses the GPU machines; cloud providers call an external API.',
      example: 'ollama',
    },
    validator_llm: {
      what: 'The model that double-checks the other agents\' extractions. Usually the same as the default.',
      example: 'qwen3:14b',
    },
    validator_llm_provider: {
      what: 'Where the validator model runs.',
      example: 'ollama',
    },
    insights_llm_provider: {
      what: 'Which LLM writes the narrative insight reports — "local" for the GPU machines, "cloud" for Azure OpenAI.',
      example: 'cloud',
    },
    insights_local_endpoint: {
      what: 'Address of the local model server used for insights when the provider is "local".',
      example: 'http://localhost:11434',
    },
    insights_local_model: {
      what: 'Which local model writes insights.',
      example: 'qwen3:14b',
    },
    insights_cloud_api_key: {
      what: 'API key for the cloud insights provider. Stored in this org\'s config file in blob, not in the code.',
      breaks: 'Wrong or expired, insight generation fails at the last phase after all the extraction work is already done.',
    },
    insights_cloud_endpoint: {
      what: 'Base URL of the cloud insights provider.',
      example: 'https://your-resource.openai.azure.com',
    },
    insights_cloud_deployment: {
      what: 'The deployment name at the cloud provider — not the model name.',
    },
    insights_cloud_api_version: {
      what: 'API version string the cloud provider expects.',
      example: '2024-02-01',
    },
  },

  // ── Config files ──────────────────────────────────────────────────────────
  files: {
    'entity_library.csv': {
      what: 'The starting vocabulary of things this org talks about — products, crops, pests — with their known alternative spellings. The pipeline matches extractions against this to decide what is real.',
      breaks: 'Too small and genuine mentions get filed as unknown; a wrong type here fixes that entity\'s type permanently, because the first agent to name an entity sets it.',
      example: 'Product,Incipio,NCPO',
    },
    'topics.json': {
      what: 'Conversation topic tags and the confidence needed to apply one.',
      breaks: 'A threshold set too low tags nearly every call with nearly every topic, which makes topic filters useless.',
    },
    'regions.json': {
      what: 'The territories this org sells into, used to group conversations regionally.',
      breaks: 'Names must match what arrives in call metadata, or conversations land in an "Unknown" region.',
    },
    'relationship_types.json': {
      what: 'The kinds of links the entity graph may draw between entities, and which signals create them.',
      breaks: 'Removing a type that existing rows already use leaves those edges pointing at a type nothing recognises.',
    },
    'outcome_config.json': {
      what: 'The outcome categories a conversation can be classified into, and the context given to the classifier. Optional — orgs without it skip outcome classification.',
      example: 'PRODUCT_SOLD, RECOMMENDATION_GIVEN',
    },
    'geo_labels.json': {
      what: 'Place-name labels drawn on the Sentiment Map. The map draws shapes but no names, so these are supplied by hand.',
    },
    'semantic_signals.xlsx': {
      what: 'The signal vocabulary spreadsheet — the phrases that map to sentiment and intent signals. Edited in Excel and uploaded.',
    },
    'zip_metadata.csv': {
      what: 'Postcode-to-region lookup table, used where call metadata carries a postcode instead of a region.',
    },
    'logo.png': {
      what: 'The org\'s logo, shown in the dashboard header.',
    },
    'passwd.json': {
      what: 'Maps the mobile app\'s user IDs to field-agent names, so recordings get attributed to a person. Created automatically when the org is made.',
      breaks: 'A missing entry leaves that agent\'s conversations with no agent_id, so they vanish from every per-agent page.',
    },
  },
};

/** Look up help for a field, returning undefined when there is none. */
export function fieldHelp(key, scope) {
  if (scope) return (ORG_FIELD_HELP[scope] || {})[key];
  return ORG_FIELD_HELP[key];
}
